import { SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── Davis Vantage WeatherLink ISS ───────────────────────────────────────────
//
// The WeatherLink v2 archive record for an Integrated Sensor Suite (ISS, data_structure_type 11)
// is shared across every Vantage model — the Vue simply never populates the solar/UV/ET fields.
// One factory keeps both models on the same gap-tolerant sub-channel layout, so a future Vantage
// model (or a Pro2 without the solar/UV/ET upgrade) slots in without renumbering anything:
//   0 imperialTemperature, 1 humidity, 2 imperialWindSpeed, 3 imperialWindGust, 4 windHeading,
//   5 digitalPrecipitation, 6 solarRadiation, 7 uvIndex, 8 imperialEvapotranspiration
// (6-8 solar/UV/ET only, per model).

type IssElementKey =
    | "imperialTemperature"
    | "humidity"
    | "imperialWindSpeed"
    | "imperialWindGust"
    | "windHeading"
    | "digitalPrecipitation"
    | "solarRadiation"
    | "uvIndex"
    | "imperialEvapotranspiration";

type DavisVantageIssParams = {
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
    /** Whether this model's ISS reports solar radiation, UV index, and evapotranspiration. */
    hasSolarSensors: boolean;
};

function davisVantageIssCluster({
    modelName,
    friendlyName,
    friendlyShortName,
    hasSolarSensors,
}: DavisVantageIssParams): SensorClusterConfig<Record<string, never>> {
    // Legal ranges reflect Davis's own published sensor specs (°F/mph/W-per-m2/UV-index converted
    // to the element's stored units), not just the shared element's broader range.
    const coreElements: SensorElementOutput<IssElementKey>[] = [
        // -40 to 150 F
        {
            sensorElementConfigKey: "imperialTemperature",
            parameters: {},
            legalConvertedRange: [-40, 65.6],
            subChannel: 0,
        },
        { sensorElementConfigKey: "humidity", parameters: {}, legalConvertedRange: [0, 100], subChannel: 1 },
        // 0 to 200 mph
        { sensorElementConfigKey: "imperialWindSpeed", parameters: {}, legalConvertedRange: [0, 89.4], subChannel: 2 },
        { sensorElementConfigKey: "imperialWindGust", parameters: {}, legalConvertedRange: [0, 89.4], subChannel: 3 },
        { sensorElementConfigKey: "windHeading", parameters: {}, legalConvertedRange: [0, 360], subChannel: 4 },
        // Interval rainfall total; no natural cap, mirrors the shared digitalPrecipitation element.
        {
            sensorElementConfigKey: "digitalPrecipitation",
            parameters: {},
            legalConvertedRange: [0, null],
            subChannel: 5,
        },
    ];

    const solarElements: SensorElementOutput<IssElementKey>[] = hasSolarSensors
        ? [
              {
                  sensorElementConfigKey: "solarRadiation",
                  parameters: {},
                  legalConvertedRange: [0, 1800],
                  subChannel: 6,
              },
              { sensorElementConfigKey: "uvIndex", parameters: {}, legalConvertedRange: [0, 16], subChannel: 7 },
              // Interval ET total; no natural cap, mirrors digitalPrecipitation's treatment above.
              {
                  sensorElementConfigKey: "imperialEvapotranspiration",
                  parameters: {},
                  legalConvertedRange: [0, null],
                  subChannel: 8,
              },
          ]
        : [];

    return {
        communicationMode: SensorPortCommunicationMode.DIGITAL,
        requiredSubChannels: [],
        optionalSubChannels: [],
        isPermanentlyAttached: true,
        parameterConfigs: {},
        manufacturerName: "Davis Instruments",
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => [...coreElements, ...solarElements],
    };
}

export const davisWeatherLinkClusterConfigs = {
    davis_vantage_vue_iss: davisVantageIssCluster({
        modelName: "Vantage Vue ISS",
        friendlyName: "Vantage Vue ISS",
        friendlyShortName: "Vue ISS",
        hasSolarSensors: false,
    }),
    davis_vantage_pro2_plus_iss: davisVantageIssCluster({
        modelName: "Vantage Pro2 Plus ISS",
        friendlyName: "Vantage Pro2 Plus ISS",
        friendlyShortName: "Pro2 Plus ISS",
        hasSolarSensors: true,
    }),

    davis_wll_barometer: {
        communicationMode: SensorPortCommunicationMode.DIGITAL,
        requiredSubChannels: [],
        optionalSubChannels: [],
        isPermanentlyAttached: true,
        parameterConfigs: {},
        manufacturerName: "Davis Instruments",
        modelName: "WeatherLink Barometer",
        friendlyName: "WeatherLink Barometer",
        friendlyShortName: "WL Barometer",
        generateSensorElements: () => [
            {
                sensorElementConfigKey: "imperialBarometricPressure",
                parameters: {},
                // Sea-level pressure world-record extremes are ~870-1085 hPa (87.0-108.5 kPa);
                // pad slightly rather than cut at the record values themselves.
                legalConvertedRange: [85, 110],
                subChannel: 0,
            },
        ],
    },

    // Stretch: inside temperature/humidity, read from the WLL gateway itself rather than the
    // outdoor ISS. Reuses the same elements as the ISS clusters above, so the i18n cluster-name
    // overrides (sensorNaming.json -> sensorCluster.davis_wll_inside_temp_hum) are what
    // distinguish "Inside Temperature/Humidity" from the outdoor readings in the UI.
    davis_wll_inside_temp_hum: {
        communicationMode: SensorPortCommunicationMode.DIGITAL,
        requiredSubChannels: [],
        optionalSubChannels: [],
        isPermanentlyAttached: true,
        parameterConfigs: {},
        manufacturerName: "Davis Instruments",
        modelName: "WeatherLink Live",
        friendlyName: "WeatherLink Inside Temperature/Humidity",
        friendlyShortName: "WL Inside T/RH",
        generateSensorElements: () => [
            // 32 to 140 F indoor range
            {
                sensorElementConfigKey: "imperialTemperature",
                parameters: {},
                legalConvertedRange: [0, 60],
                subChannel: 0,
            },
            { sensorElementConfigKey: "humidity", parameters: {}, legalConvertedRange: [0, 100], subChannel: 1 },
        ],
    },
} satisfies Record<string, SensorClusterConfig<Record<string, never>>>;
